/**
 * Thin strip under the top bar while the signed-in person has open review surveys in a cycle
 * that is in its filling phase (Oct 2026, "it's review cycle time"). The open surveys come from
 * the live to-dos; the phase from one `reviewMyAssignments` call, cached for the session and
 * refreshed when a review is submitted (REVIEWS_CHANGED_EVENT).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { ClipboardCheck } from 'lucide-react';
import { functions } from '../lib/firebase';
import { Icon } from '../design/Icon';
import { useSharedTodos } from '../notifications/TodosContext';
import { useReviewGuidance } from './guidance';
import { REVIEWS_CHANGED_EVENT } from './reviewCards';
import { CALLABLES, type ReviewMyAssignmentsResponse } from '@daaily/shared';

const callMine = httpsCallable<unknown, ReviewMyAssignmentsResponse>(functions, CALLABLES.REVIEW_MY_ASSIGNMENTS);

let cache: { fillingCycles: Set<string>; at: number } | null = null;
const CACHE_MS = 30 * 60 * 1000;

async function fillingCycleIds(force = false): Promise<Set<string>> {
  if (!force && cache && Date.now() - cache.at < CACHE_MS) return cache.fillingCycles;
  const res = (await callMine({})).data;
  const ids = new Set((res.cycles ?? []).filter((c) => c.status === 'published' && c.phase === 'filling' && c.fillOpen).map((c) => c.cycleId));
  cache = { fillingCycles: ids, at: Date.now() };
  return ids;
}

export function ReviewCycleBanner() {
  const todos = useSharedTodos();
  const g = useReviewGuidance();
  const openSurveys = todos.filter((t) => t.kind === 'review' && t.id.startsWith('review:')).length;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (openSurveys === 0) { setShow(false); return; }
    let alive = true;
    const check = (force = false) => {
      fillingCycleIds(force).then((ids) => { if (alive) setShow(ids.size > 0); }).catch(() => { if (alive) setShow(false); });
    };
    check();
    const bump = () => check(true);
    window.addEventListener(REVIEWS_CHANGED_EVENT, bump);
    return () => { alive = false; window.removeEventListener(REVIEWS_CHANGED_EVENT, bump); };
  }, [openSurveys]);

  if (!show || openSurveys === 0) return null;
  return (
    <Link to="/performance-reviews" role="status"
      className="block bg-brand-subtle text-brand border-b border-brand/20 text-xs font-medium px-4 py-1.5 text-center hover:opacity-90">
      <span className="inline-flex items-center gap-1.5">
        <Icon as={ClipboardCheck} />
        <span>{g.banner} {openSurveys === 1 ? '1 survey open.' : `${openSurveys} surveys open.`}</span>
      </span>
    </Link>
  );
}
